// crewbit-admin/src/components/money/MoneyExpenseLogPanel.tsx
//
// Single-form slide-over for logging a new expense (Decision §Expenses.D1 —
// admin doesn't chunk; mobile uses 2-step push-stack). Owns local
// ExpenseDraftState. §WorldClass.3 Phase B retired the auto-save +
// useExpenseDraft path: Save fires propose_expense_categorization with all
// operator-typed fields, then confirm_expense_proposal — every save now
// goes through the proposal+confirm pipeline (T2125-quality categorization
// matches receipt-scan flow).
//
// Propose-on-blur (vendor + amount debounced 600ms) stays for the suggestion
// banner UX; the save-time propose call is independent and creates a fresh
// proposal row with all operator fields populated. The on-blur proposal
// expires unused.
//
// "Save and add another" keeps the panel open + resets form state.
//
// Spec: docs/references/MONEY_MODULE_SPEC_v1.md §Expenses.7.2.

import { useCallback, useEffect, useRef, useState } from 'react';
import { palette, spacing, radii, transition, admin } from '../../styles/tokens';
import { SlidePanel } from '../common/SlidePanel';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Toast } from '../common/Toast';
import { MoneyInput } from '../common/MoneyInput';
import { DatePicker } from '../common/DatePicker';
import { useActiveBusinessStore } from '../../hooks/useActiveBusiness';
import { useProposeCategorization } from '../../hooks/useProposeCategorization';
import { useConfirmProposal } from '../../hooks/useConfirmProposal';
import { lookupErrorCopy } from '../../lib/errorMap';
import {
  EXPENSE_CATEGORY_GROUPS,
  PAYMENT_METHODS,
} from '../../constants/expenseCategories';
import type {
  ExpenseCategory,
  ProposedCategorization,
  ProposeCategorizationErrorCode,
  ConfirmProposalErrorCode,
  ConfirmProposalOverrides,
} from '../../types/proposals';
import ProposalSuggestionBanner from './proposals/ProposalSuggestionBanner';
import { VendorSelect } from './VendorSelect';
import { labelStyle, inputStyle } from './MoneyExpenseLogPanel.styles';

interface ExpenseDraftState {
  category: string;
  description: string | null;
  amount: number | null;
  date: string;
  vendorName: string | null;
  // Wave 7 — vendor entity FK from the picker. NULL until operator selects.
  vendorId: string | null;
  paymentMethod: string | null;
  isHstExempt: boolean;
  notes: string | null;
}

interface MoneyExpenseLogPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const todayISO = () => new Date().toISOString().split('T')[0]!;

const EMPTY: ExpenseDraftState = {
  category: '',
  description: null,
  amount: null,
  date: todayISO(),
  vendorName: null,
  vendorId: null,
  paymentMethod: null,
  isHstExempt: false,
  notes: null,
};

export function MoneyExpenseLogPanel({ isOpen, onClose }: MoneyExpenseLogPanelProps) {
  const [draft, setDraft] = useState<ExpenseDraftState>(EMPTY);
  const [amountInput, setAmountInput] = useState<string>('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [saving, setSaving] = useState(false);

  // §WorldClass.3 Phase B — full canonical save path. Propose-on-blur stays
  // for banner-suggestion UX; Save fires its own propose+confirm sequence
  // with all operator fields. Closes §WorldClass.3.AdminLogExpensePanelFullCanonical.
  const businessId = useActiveBusinessStore((s) => s.activeBusinessId);
  const proposeCategorization = useProposeCategorization();
  const confirmProposal = useConfirmProposal();
  const [proposal, setProposal] = useState<ProposedCategorization | null>(null);
  const [proposing, setProposing] = useState(false);
  const [proposalError, setProposalError] = useState<string | null>(null);
  const proposeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const proposeSeqRef = useRef(0);

  const reset = useCallback(() => {
    setDraft({ ...EMPTY, date: todayISO() });
    setAmountInput('');
    setProposal(null);
    setProposalError(null);
  }, []);

  // Reset whenever the panel re-opens. Pre-Phase-B auto-save semantics retired
  // — discarding a draft now leaves only an unconfirmed proposal row that
  // expires server-side after 7 days (no orphan expense row to archive).
  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen, reset]);

  // Cleanup pending propose debounce on unmount.
  useEffect(() => {
    return () => {
      if (proposeDebounceRef.current) clearTimeout(proposeDebounceRef.current);
    };
  }, []);

  const update = <K extends keyof ExpenseDraftState>(key: K, value: ExpenseDraftState[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const firePropose = useCallback(
    async (vendorName: string, amount: number) => {
      if (!businessId) return;
      const seq = ++proposeSeqRef.current;
      setProposing(true);
      setProposalError(null);
      try {
        const result = await proposeCategorization({
          businessId,
          vendorName,
          amount,
          description: null,
          paymentMethod: null,
          ocrData: null,
        });
        // Stale-response guard — only the latest propose call updates state.
        if (seq !== proposeSeqRef.current) return;
        if (result.success) {
          setProposal(result.data.proposed);
        } else {
          setProposal(null);
          setProposalError(
            lookupErrorCopy(
              'propose_expense_categorization',
              result.error.code as ProposeCategorizationErrorCode,
            ),
          );
        }
      } finally {
        if (seq === proposeSeqRef.current) setProposing(false);
      }
    },
    [businessId, proposeCategorization],
  );

  // Trigger propose on (vendor + amount) settled — debounced 600ms per
  // §B.3.1 D1. Fires when both fields have valid values; cancels prior
  // pending fire on each input change.
  useEffect(() => {
    if (proposeDebounceRef.current) {
      clearTimeout(proposeDebounceRef.current);
      proposeDebounceRef.current = null;
    }
    const vendorName = (draft.vendorName ?? '').trim();
    const amount = draft.amount;
    if (vendorName.length === 0 || amount === null || amount <= 0) {
      // Clear any prior proposal once inputs become invalid.
      setProposal(null);
      setProposalError(null);
      return;
    }
    proposeDebounceRef.current = setTimeout(() => {
      void firePropose(vendorName, amount);
    }, 600);
    return () => {
      if (proposeDebounceRef.current) {
        clearTimeout(proposeDebounceRef.current);
        proposeDebounceRef.current = null;
      }
    };
  }, [draft.vendorName, draft.amount, firePropose]);

  const isReady =
    !!draft.category && draft.amount !== null && draft.amount > 0 && !!businessId && !saving;
  const isDirty =
    !!draft.category ||
    draft.amount !== null ||
    (draft.vendorName ?? '').length > 0 ||
    (draft.notes ?? '').length > 0;

  const handleSave = async (closeAfter: boolean) => {
    if (!businessId || draft.amount === null) return;
    setSaving(true);
    try {
      // 1. Fire propose with all operator-typed fields. Creates a fresh
      // proposal row with operator inputs persisted; the on-blur banner
      // proposal (if any) goes unconfirmed and expires.
      const proposeResult = await proposeCategorization({
        businessId,
        vendorName: draft.vendorName,
        vendorId: draft.vendorId,
        amount: draft.amount,
        description: draft.description,
        paymentMethod: draft.paymentMethod,
        date: draft.date,
        isHstExempt: draft.isHstExempt,
        notes: draft.notes,
      });
      if (!proposeResult.success) {
        setToast({
          msg: lookupErrorCopy(
            'propose_expense_categorization',
            proposeResult.error.code as ProposeCategorizationErrorCode,
          ),
          type: 'error',
        });
        return;
      }
      // 2. Build overrides: only `category` if operator picked one different
      // from the proposal default. Other operator fields already live on the
      // proposal row (resolved from *_input columns at confirm time).
      const proposed = proposeResult.data.proposed;
      const overrides: ConfirmProposalOverrides | undefined =
        draft.category !== proposed.category
          ? { category: draft.category as ExpenseCategory }
          : undefined;
      const confirmResult = await confirmProposal({
        proposalId: proposeResult.data.proposal_id,
        action: overrides ? 'confirm_with_overrides' : 'confirm',
        overrides,
      });
      if (confirmResult.success) {
        setToast({ msg: 'Expense logged', type: 'success' });
        if (closeAfter) onClose();
        else reset();
      } else {
        setToast({
          msg: lookupErrorCopy(
            'confirm_expense_proposal',
            confirmResult.error.code as ConfirmProposalErrorCode,
          ),
          type: 'error',
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (isDirty) setConfirmDiscard(true);
    else onClose();
  };


  return (
    <>
      <SlidePanel
        isOpen={isOpen}
        onClose={handleClose}
        title="Log expense"
        subtitle={saving ? 'Saving…' : undefined}
        width={560}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          {/* Vendor + Amount — Wave 7: freetext replaced by VendorSelect */}
          <div style={{ display: 'flex', gap: spacing.md }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Vendor</label>
              <VendorSelect
                vendorId={draft.vendorId}
                vendorName={draft.vendorName}
                onChange={(vendorId, vendorName) => {
                  setDraft((d) => ({ ...d, vendorId, vendorName }));
                }}
              />
            </div>
            <div style={{ width: 180 }}>
              <label style={labelStyle}>Amount *</label>
              <MoneyInput
                value={amountInput}
                onChange={(v) => {
                  setAmountInput(v);
                  const n = Number(v);
                  update('amount', !isNaN(n) && n > 0 ? n : null);
                }}
              />
            </div>
          </div>

          {/* Date + Payment method */}
          <div style={{ display: 'flex', gap: spacing.md }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Date *</label>
              <DatePicker
                value={draft.date}
                onChange={(v) => update('date', v ?? todayISO())}
                maxDate={todayISO()}
                clearable={false}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Payment method</label>
              <select
                value={draft.paymentMethod ?? ''}
                onChange={(e) => update('paymentMethod', e.target.value || null)}
                style={{ ...inputStyle, color: draft.paymentMethod ? palette.g800 : palette.g400 }}
              >
                <option value="">Select…</option>
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Proposal display (Tier 2-UI Phase B Phase B — display-only per
              Decision §T2UI.PhaseB-admin.D1; no apply-to-picker action). */}
          <ProposalSuggestionBanner
            proposal={proposal}
            proposing={proposing}
            proposalError={proposalError}
          />

          {/* Category */}
          <div>
            <label style={labelStyle}>Category *</label>
            <select
              value={draft.category}
              onChange={(e) => update('category', e.target.value)}
              style={{ ...inputStyle, color: draft.category ? palette.g800 : palette.g400 }}
            >
              <option value="">Select category…</option>
              {EXPENSE_CATEGORY_GROUPS.map((g) => (
                <optgroup key={g.group} label={g.label}>
                  {g.subcategories.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label style={labelStyle}>Description</label>
            <input
              value={draft.description ?? ''}
              onChange={(e) => update('description', e.target.value || null)}
              placeholder="What was this for?"
              style={inputStyle}
            />
          </div>

          {/* HST exempt + Notes */}
          <div>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                color: palette.g700,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={draft.isHstExempt}
                onChange={(e) => update('isHstExempt', e.target.checked)}
              />
              Mark as HST exempt
            </label>
          </div>

          <div>
            <label style={labelStyle}>Notes</label>
            <textarea
              value={draft.notes ?? ''}
              onChange={(e) => update('notes', e.target.value || null)}
              rows={3}
              placeholder="Additional details…"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
        </div>

        <div
          style={{
            position: 'sticky',
            bottom: -20,
            marginLeft: -20,
            marginRight: -20,
            marginTop: spacing.lg,
            padding: `${spacing.md}px ${spacing.lg}px`,
            background: palette.white,
            borderTop: `1px solid ${admin.border}`,
            display: 'flex',
            gap: spacing.sm,
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            onClick={handleClose}
            style={{
              padding: '9px 18px',
              borderRadius: radii.sm,
              border: `1px solid ${palette.g200}`,
              background: palette.white,
              fontSize: 13,
              fontWeight: 600,
              color: palette.g600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: `background ${transition.fast}`,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!isReady}
            onClick={() => void handleSave(false)}
            style={{
              padding: '9px 18px',
              borderRadius: radii.sm,
              border: `1px solid ${palette.accent}`,
              background: palette.white,
              color: palette.accent,
              fontSize: 13,
              fontWeight: 600,
              cursor: isReady ? 'pointer' : 'not-allowed',
              opacity: isReady ? 1 : 0.5,
              fontFamily: 'inherit',
            }}
          >
            Save and add another
          </button>
          <button
            type="button"
            disabled={!isReady}
            onClick={() => void handleSave(true)}
            style={{
              padding: '9px 22px',
              borderRadius: radii.sm,
              border: 'none',
              background: isReady ? palette.accent : palette.g300,
              color: palette.white,
              fontSize: 13,
              fontWeight: 600,
              cursor: isReady ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
            }}
          >
            Save expense
          </button>
        </div>
      </SlidePanel>

      <ConfirmDialog
        isOpen={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          onClose();
        }}
        title="Discard this expense?"
        message="You have unsaved changes that will be lost."
        confirmLabel="Discard"
        confirmVariant="danger"
      />

      {toast && (
        <Toast
          message={toast.msg}
          type={toast.type}
          isVisible={!!toast}
          onDismiss={() => setToast(null)}
        />
      )}
    </>
  );
}
